import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

interface AdBlockProps {
  className?: string;
  adSlot?: string;
  format?: 'social-bar' | 'banner' | 'native' | 'interstitial' | 'floating';
  mobileOnly?: boolean;
  desktopOnly?: boolean;
  position?: 'top' | 'bottom' | 'middle' | 'sticky-bottom' | 'sticky-top';
  requireGestureOnMobile?: boolean;
  useSandboxOnMobile?: boolean;
}

const AdContainer = styled.div<{ 
  $mobileOnly?: boolean; 
  $desktopOnly?: boolean;
  $position?: 'top' | 'bottom' | 'middle' | 'sticky-bottom' | 'sticky-top';
  $format?: string;
}>`
  min-height: ${props => props.$format === 'floating' ? '50px' : '90px'};
  margin: 0.5rem auto;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  background: transparent;
  border-radius: 8px;
  padding: 4px;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 728px;
  z-index: ${props => props.$format === 'floating' || props.$position?.includes('sticky') ? '999' : '1'};
  
  /* Affichage conditionnel selon le type d'appareil */
  display: ${props => props.$mobileOnly ? 'none' : 'flex'};
  
  /* Positionnement spécial */
  ${props => props.$position === 'sticky-bottom' && `
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    margin: 0;
    border-radius: 8px 8px 0 0;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  `}
  
  ${props => props.$position === 'sticky-top' && `
    position: sticky;
    top: 0;
    margin: 0;
    border-radius: 0 0 8px 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  `}
  
  /* Styles spécifiques pour mobile */
  @media (max-width: 768px) {
    display: ${props => props.$desktopOnly ? 'none' : 'flex'};
    min-height: ${props => {
      if (props.$format === 'interstitial') return '250px';
      if (props.$format === 'floating') return '50px';
      return '60px';
    }};
    margin: ${props => props.$position?.includes('sticky') ? '0' : '0.25rem 0'};
    padding: 2px;
  }
  
  /* Styles spécifiques pour desktop */
  @media (min-width: 769px) {
    display: ${props => props.$mobileOnly ? 'none' : 'flex'};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    padding: 8px;
    max-height: 150px;
    overflow: hidden;
    
    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
    
    /* Limiter la taille des iframes et images */
    iframe, img, div {
      max-height: 140px !important;
      object-fit: contain;
    }
  }
  
  /* Styles spécifiques pour les formats */
  ${props => props.$format === 'interstitial' && `
    @media (max-width: 768px) {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 90%;
      height: auto;
      min-height: 250px;
      z-index: 1000;
      background: rgba(0, 0, 0, 0.7);
      backdrop-filter: blur(5px);
      border-radius: 12px;
      padding: 15px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
    }
  `}
  
  ${props => props.$format === 'floating' && `
    @media (max-width: 768px) {
      position: fixed;
      bottom: 10px;
      left: 50%;
      transform: translateX(-50%);
      width: 320px;
      height: 50px;
      z-index: 999;
      border-radius: 25px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  `}
`;

const AdBlock: React.FC<AdBlockProps> = ({ 
  className, 
  adSlot = 'default', 
  format = 'banner', 
  mobileOnly = false, 
  desktopOnly = false,
  position,
  requireGestureOnMobile = false,
  useSandboxOnMobile = true,
}) => {
  const adRef = useRef<HTMLDivElement>(null);
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // One-shot guard to avoid double-injection from multiple triggers (gesture + consent + fallback)
  const loadedRef = useRef(false);

  useEffect(() => {
    const loadAd = () => {
      // Dedupe early to avoid concurrent double-load
      if (loadedRef.current) return;
      loadedRef.current = true;
      try {
        if (window.atAsyncOptions) {
          window.atAsyncOptions.renderStarted = true;
          window.atAsyncOptions.started = true;
        }
        
        // Sélectionner le script approprié en fonction du format et de l'appareil
        let scriptSrc = 'https://pl26545116.profitableratecpm.com/37e911c662696fd250812aea75ee105c/invoke.js';
        
        // Sélection du script en fonction du format et de l'appareil
        if (window.innerWidth <= 768) {
          // Scripts optimisés pour mobile selon le format
          switch(format) {
            case 'banner':
              scriptSrc = 'https://pl26545116.profitableratecpm.com/37e911c662696fd250812aea75ee105c/invoke.js';
              break;
            case 'interstitial':
              scriptSrc = 'https://pl26545100.profitableratecpm.com/9c/05/e1/9c05e1de5e8d6e8a6db1527676d71e17.js';
              break;
            case 'floating':
              scriptSrc = 'https://pl26545100.profitableratecpm.com/f4/4d/e3/f44de3a0cd7c8c9f85c92b5f2f5c8b8d.js';
              break;
            case 'native':
              scriptSrc = 'https://pl26545116.profitableratecpm.com/37e911c662696fd250812aea75ee105c/invoke.js';
              break;
            default:
              scriptSrc = 'https://pl26545116.profitableratecpm.com/37e911c662696fd250812aea75ee105c/invoke.js';
          }
        }
        
        // Security: sandbox any ad iframe injected into the container (mobile only)
        const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
        const applyIframeSafety = (ifr: HTMLIFrameElement) => {
          try {
            if (!isMobile) return;
            if (!ifr.getAttribute('sandbox')) {
              // Minimal sandbox on mobile: block popups by default
              ifr.setAttribute('sandbox', 'allow-scripts allow-same-origin');
            }
            ifr.setAttribute('referrerpolicy', 'no-referrer');
          } catch {}
        };
        const mo = adRef.current ? new MutationObserver((mutations) => {
          for (const m of mutations) {
            m.addedNodes.forEach((n) => {
              if (n.nodeType === Node.ELEMENT_NODE) {
                const el = n as Element;
                if (el.tagName === 'IFRAME') applyIframeSafety(el as HTMLIFrameElement);
                el.querySelectorAll && el.querySelectorAll('iframe').forEach((ifr) => applyIframeSafety(ifr as HTMLIFrameElement));
              }
            });
          }
        }) : null;
        try { mo?.observe(adRef.current as Node, { childList: true, subtree: true }); } catch {}

        // Tentative de rechargement de la publicité
        if (adRef.current) {
          // Éviter les doublons: nettoyer avant d'injecter
          adRef.current.innerHTML = '';
          if (isMobile && useSandboxOnMobile) {
            // Exécuter la pub dans une iframe sandboxée (empêche les redirections top-level)
            const iframe = document.createElement('iframe');
            iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin');
            iframe.setAttribute('referrerpolicy', 'no-referrer');
            iframe.style.width = '100%';
            iframe.style.height = format === 'interstitial' ? '250px' : '60px';
            iframe.style.border = '0';
            adRef.current.appendChild(iframe);
            const doc = iframe.contentDocument || (iframe as any).document;
            if (doc) {
              const s = doc.createElement('script');
              s.src = scriptSrc;
              s.async = true;
              try { (s as any).referrerPolicy = 'no-referrer'; } catch {}
              try { (s as any).crossOrigin = 'anonymous'; } catch {}
              doc.body.appendChild(s);
            }
          } else {
            const script = document.createElement('script');
            script.src = scriptSrc;
            script.async = true;
            try { (script as any).referrerPolicy = 'no-referrer'; } catch {}
            try { (script as any).crossOrigin = 'anonymous'; } catch {}
            script.onerror = (error) => {
              console.error('Error loading ad script:', error);
            };
            adRef.current.appendChild(script);
          }
          // Mark as loaded on container as well
          try { (adRef.current as any).dataset.adLoaded = '1'; } catch {}
        }
      } catch (error) {
        console.error('Error in loadAd:', error);
      }
    };

    
    const handleConsent = (e: any) => {
      if (e.detail?.event === 'consent_status') {
        loadAd();
      }
    };

    window.addEventListener('ucEvent', handleConsent);
    
    // Charger la publicité avec contraintes mobile
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    const startLoad = () => {
      // Respecter les flags mobileOnly/desktopOnly
      if ((mobileOnly && window.innerWidth > 768) || (desktopOnly && window.innerWidth <= 768)) {
        return;
      }
      loadAd();
    };
    const startOrGateByConsent = () => {
      if (window.UC_UI?.isInitialized()) {
        startLoad();
      } else {
        // Fallback: si aucun CMP/UC_UI n'est présent, charger après un court délai
        fallbackTimerRef.current = setTimeout(startLoad, 600);
      }
    };
    if (isMobile && requireGestureOnMobile) {
      let onGesture: any = null;
      onGesture = () => {
        try { window.removeEventListener('touchstart', onGesture, true); } catch {}
        try { window.removeEventListener('click', onGesture, true); } catch {}
        startOrGateByConsent();
      };
      try { window.addEventListener('touchstart', onGesture, { capture: true, passive: true }); } catch {}
      try { window.addEventListener('click', onGesture, { capture: true, passive: true }); } catch {}
      // Cleanup gesture listeners on unmount below
      (adRef.current as any).__onGesture = onGesture;
    } else {
      startOrGateByConsent();
    }

    // Ajouter un écouteur pour redimensionner les publicités lors du changement de taille d'écran
    const handleResize = () => {
      if ((mobileOnly && window.innerWidth > 768) || (desktopOnly && window.innerWidth <= 768)) {
        // Ne pas charger la publicité si elle ne doit pas être affichée sur cet appareil
        return;
      }
      // Allow a single reload on resize/orientation change
      loadedRef.current = false;
      loadAd();
    };

    window.addEventListener('resize', handleResize);
    const onVis = () => { if (document.visibilityState === 'visible' && !loadedRef.current) startOrGateByConsent(); };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      window.removeEventListener('ucEvent', handleConsent);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', onVis);
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
      }
      // Cleanup gesture handlers if any
      const onGesture = (adRef.current as any)?.__onGesture;
      if (onGesture) {
        try { window.removeEventListener('touchstart', onGesture, true); } catch {}
        try { window.removeEventListener('click', onGesture, true); } catch {}
      }
      loadedRef.current = false;
    };
  }, [adSlot, format, mobileOnly, desktopOnly]);

  return (
    <AdContainer 
      ref={adRef} 
      className={className} 
      data-ad-slot={adSlot} 
      data-ad-format={format}
      $mobileOnly={mobileOnly}
      $desktopOnly={desktopOnly}
      $position={position}
      $format={format}
    />
  );
};

export { AdBlock };

// Types pour TypeScript
declare global {
  // atAsyncOptions est déjà défini dans src/types/adsterra.d.ts
  interface Window {
    UC_UI?: {
      isInitialized: () => boolean;
    };
  }
}
